/* ============================================================
   UNIVERSAL LEDGER LOADER — Phase 1.5 (Agnostic Purification)
   Single source of truth for historical actuals in Lens.

   Reads from:
     1. API contract             — Cleic Ledger /api/ledger.json (F-022)
     2. Snapshot file            — /ledger.json or /ledger-monthly.json (legacy / offline)
     3. Synthetic                — engine.backCastActuals (2% drift, zero-data mode)

   Strict API mode is fail-closed: when VITE_LEDGER_SOURCE=api, Lens does not
   fall through to static snapshots or synthetic actuals. It either loads the
   private Ledger contract for an explicit Book or marks the contract posture
   HOLD.

   Contract lives at 3-resources/canon/patterns/P-atomic-ledger-contract.md.

   Auth: API mode injects Authorization: Bearer <jwt> from the same Supabase
   session the auth-gate uses (per P-019 Auth Gate ≠ Auth Propagation).
   Without this, the Cleic Ledger Hono auth middleware 401s every call —
   strict API mode marks the contract posture HOLD rather than loading
   fallback actuals.

   Multi-tenancy: Lens requires an explicit Book or Shelf scope. Without a
   user-selected scope, every source mode holds instead of loading bundled
   snapshot or synthetic actuals.

   Expose:
     - loadMonthlyLedger()       — async preloader, call once on app mount
     - getMonthlyLedgerCache()   — sync accessor for engine.js
     - getLedgerSource()         — 'contract' | 'snapshot' | 'synthetic' — legacy UI badge
     - getLedgerSourceState()    — explicit D1 source-state class + safety labels
   ============================================================ */

import { supabase } from './lib/supabase.js';
import { resolveCleicAppUrl } from './app-channel.js';
import {
  hydrateLensBookScopeFromBrowser,
  normalizeBookScopeBasis,
  normalizeBookScopeId,
  persistLensBookScope,
} from './book-scope.js';
import {
  SOURCE_STATE_CLASSES,
  normalizeProviderHealth,
  normalizeSourceState,
  uniqueWarningCodes,
} from './source-state.js';
import {
  setMonthlyLedgerCache,
  setLedgerCommercialTimingSignals,
  getMonthlyLedgerCache as _sharedGetCache,
  getMonthlyLedgerSource as _sharedGetSource,
} from './ledger-cache.js';

/* F-022 Ledger ↔ Lens siblings — config.
   When VITE_LEDGER_SOURCE=api, fetch the atomic-ledger-contract shape from
   Cleic Ledger's /api/ledger.json endpoint. Other modes try bundled
   snapshot files before synthetic local modeling. */
const LEDGER_SOURCE_MODE = (import.meta.env.VITE_LEDGER_SOURCE || 'api').toLowerCase();
const LEDGER_API_URL     = resolveCleicAppUrl(
  'ledger',
  import.meta.env.VITE_FINANCE_API_URL || 'https://ledger.cleic.com',
);
const INITIAL_BOOK_SCOPE = hydrateLensBookScopeFromBrowser();
const INITIAL_HAS_ACTIVE_SCOPE = !!(INITIAL_BOOK_SCOPE.bookId || INITIAL_BOOK_SCOPE.shelfId);

let _loadPromise = null;
let _activeBookId = INITIAL_BOOK_SCOPE.bookId || null;
let _activeShelfId = INITIAL_BOOK_SCOPE.shelfId || null;
let _activeBookScopeBasis = INITIAL_BOOK_SCOPE.bookId || INITIAL_BOOK_SCOPE.shelfId
  ? normalizeBookScopeBasis(INITIAL_BOOK_SCOPE.basis, 'ledger-url-handoff')
  : null;
let _activeBookWasAutoPicked = false;
let _contractFallbackReason = INITIAL_HAS_ACTIVE_SCOPE
  ? (LEDGER_SOURCE_MODE === 'api'
      ? 'contract source not loaded yet'
      : 'static snapshot mode selected')
  : 'explicit Lens Book or Shelf scope required before loading actuals';
let _providerHealth = normalizeProviderHealth(null);
let _ledgerSourceState = normalizeSourceState({
  sourceClass: INITIAL_HAS_ACTIVE_SCOPE
    ? (LEDGER_SOURCE_MODE === 'api'
        ? SOURCE_STATE_CLASSES.CONTRACT_REAL
        : SOURCE_STATE_CLASSES.SNAPSHOT_STATIC)
    : SOURCE_STATE_CLASSES.MISSING_WARNING,
  mode: LEDGER_SOURCE_MODE,
  fallbackTier: INITIAL_HAS_ACTIVE_SCOPE ? 'pending' : 'scope-hold',
  fallbackReason: _contractFallbackReason,
  hasExplicitBook: !!_activeBookId,
  hasActiveBook: !!_activeBookId,
  hasActiveScope: INITIAL_HAS_ACTIVE_SCOPE,
  autoPickedBook: false,
  bookScope: makeActiveBookScopePatch(),
  providerHealth: _providerHealth,
  canClaimStrictRealBook: false,
  warning: INITIAL_HAS_ACTIVE_SCOPE
    ? 'Pending Lens source load; do not treat as strict real-book proof.'
    : 'No Book or Shelf is selected; Lens holds at zero and does not load snapshot or synthetic actuals.',
});
let _ledgerActualsSnapshot = null;

/* Re-export the shared getters so existing callers (App.jsx) keep working. */
export function getMonthlyLedgerCache() { return _sharedGetCache(); }
export function getLedgerSource()       { return _sharedGetSource(); }
export function getActiveLensBookId()   { return _activeBookId; }
export function getActiveLensBookScopeSelection() {
  return {
    bookId: _activeBookId,
    shelfId: _activeShelfId,
    basis: _activeBookScopeBasis,
  };
}
export function hasActiveLensBookScope() { return !!(_activeBookId || _activeShelfId); }
export function getLedgerSourceState()  { return _ledgerSourceState; }
export function getLedgerActualsSnapshot() { return _ledgerActualsSnapshot; }

function makeActiveBookScopePatch(extra = {}) {
  if (!_activeBookId && !_activeShelfId) return null;
  const activeScope = _activeShelfId ? 'shelf' : 'book';
  return {
    bookId: _activeBookId,
    shelfId: _activeShelfId,
    activeScope,
    basis: _activeBookScopeBasis || (activeScope === 'shelf' ? 'x-shelf-id' : 'x-book-id'),
    explicitBook: activeScope === 'book',
    autoPickedBook: false,
    tenantMembershipVerified: null,
    canClaimStrictRealBook: false,
    ...extra,
  };
}

function makeLedgerSourceState(source, patch = {}) {
  const sourceClass = patch.sourceClass || (source === 'contract'
    ? SOURCE_STATE_CLASSES.CONTRACT_REAL
    : source === 'snapshot'
      ? SOURCE_STATE_CLASSES.SNAPSHOT_STATIC
      : SOURCE_STATE_CLASSES.SYNTHETIC_DERIVED);
  const contractCurrentness = patch.contractCurrentness || null;
  const bookScope = patch.bookScope || null;
  const bookSettings = patch.bookSettings || null;
  const reportProof = patch.reportProof || null;
  const reportLineAuthorship = patch.reportLineAuthorship || null;
  const moneyProvenance = patch.moneyProvenance || null;
  const providerHealth = source === 'contract'
    ? normalizeProviderHealth(patch.providerHealth || _providerHealth)
    : normalizeProviderHealth(null);
  const dataTier = reportLineAuthorship?.dataTier
    || moneyProvenance?.dataTier
    || reportProof?.dataTier
    || contractCurrentness?.dataTier
    || null;
  const contractWarningCodes = uniqueWarningCodes(
    patch.contractWarningCodes,
    contractCurrentness,
    reportProof,
    reportLineAuthorship,
    moneyProvenance,
  );
  const fallbackReason = patch.fallbackReason ?? (
    source === 'contract'
      ? null
      : source === 'snapshot'
        ? _contractFallbackReason
        : (_contractFallbackReason || 'snapshot source unavailable; using synthetic-derived local model')
  );
  const hasExplicitBook = bookScope?.explicitBook ?? !!_activeBookId;
  const hasActiveBook = !!(bookScope?.bookId ?? _activeBookId);
  const hasActiveScope = !!(bookScope?.bookId ?? bookScope?.shelfId ?? _activeBookId ?? _activeShelfId);
  const autoPickedBook = bookScope?.autoPickedBook ?? _activeBookWasAutoPicked;
  return normalizeSourceState({
    sourceClass,
    kind: source,
    mode: LEDGER_SOURCE_MODE,
    fallbackTier: patch.fallbackTier || source,
    fallbackReason,
    hasExplicitBook,
    hasActiveBook,
    hasActiveScope,
    autoPickedBook,
    contractCurrentness,
    dataTier,
    bookScope,
    bookSettings,
    reportProof,
    reportLineAuthorship,
    moneyProvenance,
    providerHealth,
    contractWarningCodes,
    canClaimStrictRealBook:
      source === 'contract' &&
      LEDGER_SOURCE_MODE === 'api' &&
      contractCurrentness?.noFallback === true &&
      bookScope?.canClaimStrictRealBook === true &&
      bookScope?.tenantMembershipVerified === true &&
      bookScope?.explicitBook === true &&
      bookScope?.autoPickedBook !== true &&
      !fallbackReason,
    warning: patch.warning,
  });
}

/* P-019 — pull JWT from the same Supabase client the auth-gate uses.
   In strict API mode, null means HOLD; snapshot mode still uses local files. */
async function getBearerHeader() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ? `Bearer ${session.access_token}` : null;
  } catch {
    return null;
  }
}

function makeLedgerHeaders(bearer) {
  const headers = {};
  if (bearer) headers['Authorization'] = bearer;
  if (_activeShelfId) {
    headers['x-shelf-id'] = _activeShelfId;
    headers['x-lens-scope-type'] = 'shelf';
    headers['x-lens-book-scope-basis'] = _activeBookScopeBasis || 'x-shelf-id';
    headers['x-lens-auto-picked-book'] = 'false';
  } else if (_activeBookId) {
    headers['x-book-id'] = _activeBookId;
    headers['x-lens-scope-type'] = 'book';
    headers['x-lens-book-scope-basis'] = _activeBookScopeBasis || 'x-book-id';
    headers['x-lens-auto-picked-book'] = 'false';
  }
  return headers;
}

function makeLedgerCatalogHeaders(bearer) {
  const headers = {};
  if (bearer) headers['Authorization'] = bearer;
  return headers;
}

async function fetchLedgerCatalogPath(path, bearer) {
  const res = await fetch(`${LEDGER_API_URL.replace(/\/+$/, '')}/api${path}`, {
    cache: 'no-cache',
    headers: makeLedgerCatalogHeaders(bearer),
  });
  if (!res.ok) throw new Error(`Ledger catalog request returned HTTP ${res.status}`);
  return res.json();
}

export async function fetchLensBookScopeCatalog() {
  const bearer = await getBearerHeader();
  if (!bearer) throw new Error('no private auth session for Ledger Books/Shelves');

  const [books, shelfData] = await Promise.all([
    fetchLedgerCatalogPath('/books', bearer),
    fetchLedgerCatalogPath('/book-shelves', bearer),
  ]);

  return {
    books: Array.isArray(books) ? books : [],
    shelves: Array.isArray(shelfData?.shelves) ? shelfData.shelves : [],
    flatShelves: Array.isArray(shelfData?.flatShelves) ? shelfData.flatShelves : [],
    bookLinks: Array.isArray(shelfData?.bookLinks) ? shelfData.bookLinks : [],
    scopeExpansions: Array.isArray(shelfData?.scopeExpansions) ? shelfData.scopeExpansions : [],
    unshelvedBookIds: Array.isArray(shelfData?.unshelvedBookIds) ? shelfData.unshelvedBookIds : [],
  };
}

function resetLedgerLoadCaches(source = 'pending') {
  _loadPromise = null;
  _ledgerActualsSnapshot = null;
  setMonthlyLedgerCache(null, source);
  setLedgerCommercialTimingSignals(null, source);
}

export function setActiveLensBookScopeSelection(selection = {}) {
  const bookId = normalizeBookScopeId(selection.bookId);
  const shelfId = normalizeBookScopeId(selection.shelfId);
  const hasScope = !!(shelfId || bookId);
  const basis = hasScope
    ? normalizeBookScopeBasis(selection.basis, 'lens-selector')
    : null;

  _activeBookId = bookId;
  _activeShelfId = shelfId;
  _activeBookScopeBasis = basis;
  _activeBookWasAutoPicked = false;
  persistLensBookScope({ bookId, shelfId, basis }, typeof window === 'undefined' ? null : window.localStorage);
  resetLedgerLoadCaches(LEDGER_SOURCE_MODE === 'api' ? 'contract' : 'snapshot');

  _contractFallbackReason = hasScope
    ? `Lens ${shelfId ? 'Shelf' : 'Book'} scope changed; contract source reload pending`
    : 'explicit Lens scope cleared';
  _ledgerSourceState = makeLedgerSourceState(LEDGER_SOURCE_MODE === 'api' ? 'contract' : 'snapshot', {
    sourceClass: hasScope && LEDGER_SOURCE_MODE !== 'api'
      ? SOURCE_STATE_CLASSES.SNAPSHOT_STATIC
      : SOURCE_STATE_CLASSES.MISSING_WARNING,
    fallbackTier: hasScope
      ? (LEDGER_SOURCE_MODE === 'api' ? 'api-hold' : 'pending')
      : 'scope-hold',
    fallbackReason: _contractFallbackReason,
    hasActiveScope: hasScope,
    bookScope: makeActiveBookScopePatch(),
    warning: hasScope
      ? `Lens ${shelfId ? 'Shelf' : 'Book'} scope changed; contract source reload pending.`
      : 'Explicit Book or Shelf scope is required before Lens can load strict Ledger contract actuals.',
  });

  return getActiveLensBookScopeSelection();
}

async function fetchProviderHealth(bearer) {
  if (LEDGER_SOURCE_MODE !== 'api') return normalizeProviderHealth(null);
  if (!bearer) return normalizeProviderHealth(null);

  try {
    const res = await fetch(`${LEDGER_API_URL.replace(/\/+$/, '')}/api/exchange-rates/provider-health`, {
      cache: 'no-cache',
      headers: makeLedgerHeaders(bearer),
    });
    if (!res.ok) {
      return normalizeProviderHealth({
        checkedAt: new Date().toISOString(),
        liveStatus: 'unavailable',
        holdReason: `provider-health request returned HTTP ${res.status}`,
      });
    }
    return normalizeProviderHealth(await res.json());
  } catch {
    return normalizeProviderHealth({
      checkedAt: new Date().toISOString(),
      liveStatus: 'unavailable',
      holdReason: 'provider-health fetch failed or returned invalid JSON',
    });
  }
}

function markApiContractHold(reason, warning = 'Strict API mode failed closed; no snapshot or synthetic fallback was loaded.') {
  _contractFallbackReason = reason;
  _ledgerActualsSnapshot = null;
  setLedgerCommercialTimingSignals(null, 'contract');
  _ledgerSourceState = makeLedgerSourceState('contract', {
    sourceClass: SOURCE_STATE_CLASSES.MISSING_WARNING,
    fallbackTier: 'api-hold',
    fallbackReason: reason,
    hasExplicitBook: !!_activeBookId,
    hasActiveBook: !!_activeBookId,
    hasActiveScope: !!(_activeBookId || _activeShelfId),
    autoPickedBook: false,
    bookScope: makeActiveBookScopePatch(),
    warning,
  });
  console.info(`[data-ledger] strict API HOLD: ${reason}`);
  return null;
}

function markLensScopeHold(
  reason = 'explicit Lens Book or Shelf scope required before loading actuals',
  warning = 'No Book or Shelf is selected; Lens holds at zero and does not load snapshot or synthetic actuals.'
) {
  _contractFallbackReason = reason;
  _ledgerActualsSnapshot = null;
  setLedgerCommercialTimingSignals(null, LEDGER_SOURCE_MODE === 'api' ? 'contract' : 'snapshot');
  _ledgerSourceState = makeLedgerSourceState(LEDGER_SOURCE_MODE === 'api' ? 'contract' : 'snapshot', {
    sourceClass: SOURCE_STATE_CLASSES.MISSING_WARNING,
    fallbackTier: 'scope-hold',
    fallbackReason: reason,
    hasExplicitBook: false,
    hasActiveBook: false,
    hasActiveScope: false,
    autoPickedBook: false,
    bookScope: null,
    warning,
  });
  console.info(`[data-ledger] scope HOLD: ${reason}`);
  return null;
}

function extractContractProof(json) {
  return {
    contractCurrentness: json?.meta?.currentness || json?.currentness || null,
    bookScope: json?.meta?.bookScope || json?.bookScope || null,
    bookSettings: json?.meta?.bookSettings || json?.bookSettings || null,
    reportProof: json?.meta?.reportProof || json?.reportProof || null,
    reportLineAuthorship: json?.meta?.reportLineAuthorship || json?.reportLineAuthorship || null,
    moneyProvenance: json?.meta?.moneyProvenance || json?.moneyProvenance || null,
  };
}

function ledgerMoneyNumber(value) {
  const raw = value && typeof value === 'object' && 'amount' in value ? value.amount : value;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function ledgerCurrencyCode(value) {
  return typeof value === 'string' && value.trim()
    ? value.trim().toUpperCase()
    : null;
}

function ledgerMoneyCurrency(value) {
  if (!value || typeof value !== 'object') return null;
  return ledgerCurrencyCode(value.currency || value.currencyCode || value.code);
}

function ledgerLineFallbackCurrency(line, bookSettings, baseCurrency) {
  return ledgerCurrencyCode(line?.currency || line?.currencyCode)
    || ledgerMoneyCurrency(line?.amounts?.adjusted?.signedAmount)
    || ledgerMoneyCurrency(line?.amounts?.signedAmount)
    || ledgerMoneyCurrency(line?.amounts?.debit)
    || ledgerMoneyCurrency(line?.amounts?.credit)
    || ledgerCurrencyCode(bookSettings?.defaultCurrency)
    || ledgerCurrencyCode(baseCurrency)
    || null;
}

function ledgerAccountType(account) {
  return String(account?.type || account?.accountType || '').toLowerCase();
}

function ledgerAccountSubtype(account) {
  return String(account?.subtype || account?.accountSubType || '').toLowerCase();
}

function ledgerAccountName(account) {
  return String(account?.name || account?.accountName || account?.label || '').toLowerCase();
}

function isLiquidLedgerAccount(account) {
  const haystack = `${ledgerAccountSubtype(account)} ${ledgerAccountName(account)} ${String(account?.naturalKey || '').toLowerCase()}`;
  return ledgerAccountType(account) === 'asset' && /cash|bank|checking|savings|wallet|petty/.test(haystack);
}

function isCardLedgerLiability(account) {
  const haystack = `${ledgerAccountSubtype(account)} ${ledgerAccountName(account)} ${String(account?.naturalKey || '').toLowerCase()}`;
  return ledgerAccountType(account) === 'liability' && /credit card|card|revolving|short-term|current/.test(haystack);
}

function reportLineAmountSelection(line, { bookSettings = null, baseCurrency = null } = {}) {
  const reportingCurrency = ledgerCurrencyCode(bookSettings?.reportingCurrency);
  const defaultCurrency = ledgerCurrencyCode(bookSettings?.defaultCurrency) || ledgerCurrencyCode(baseCurrency);
  const presentationCurrency = ledgerMoneyCurrency(line?.amounts?.presentationAmount) || reportingCurrency;
  if (line?.amounts?.presentationAmount) {
    return {
      amount: ledgerMoneyNumber(line.amounts.presentationAmount),
      currency: presentationCurrency,
      basis: 'presentationAmount',
    };
  }

  const fallback = line?.amounts?.adjusted?.signedAmount
    ? { value: line.amounts.adjusted.signedAmount, basis: 'adjusted.signedAmount', sign: 1 }
    : line?.amounts?.signedAmount
      ? { value: line.amounts.signedAmount, basis: 'signedAmount', sign: 1 }
      : line?.amounts?.debit
        ? { value: line.amounts.debit, basis: 'debit', sign: 1 }
        : line?.amounts?.credit
          ? { value: line.amounts.credit, basis: 'credit', sign: -1 }
          : null;
  const fallbackCurrency = ledgerMoneyCurrency(fallback?.value) || ledgerLineFallbackCurrency(line, bookSettings, baseCurrency);
  const reportingDiffers = Boolean(reportingCurrency && defaultCurrency && reportingCurrency !== defaultCurrency);
  const unconverted = Boolean(reportingDiffers);
  const currency = unconverted ? (fallbackCurrency || defaultCurrency) : (fallbackCurrency || presentationCurrency || defaultCurrency);
  const amount = fallback ? ledgerMoneyNumber(fallback.value) * fallback.sign : 0;
  const moneyProvenance = unconverted
    ? {
        providerMode: 'missing',
        rateSource: 'missing',
        rateResolution: 'MISSING',
        fallbackReason: 'presentationAmount missing while Ledger reporting currency differs from Book/default currency',
        reviewReason: 'Lens preserved the source/default amount currency instead of labeling it as reporting currency.',
        originCurrency: currency,
        quoteCurrency: reportingCurrency,
        bookDefaultCurrency: defaultCurrency,
        currencyBasis: {
          bookDefaultCurrency: defaultCurrency,
          displayCurrency: currency,
          reportingCurrency,
        },
        warningCodes: ['ledger-reporting-currency-unconverted-row'],
      }
    : null;
  return {
    amount,
    currency,
    basis: fallback?.basis || 'missing',
    moneyProvenance,
    unconvertedReportingCurrency: unconverted
      ? {
          amountCurrency: currency,
          reportingCurrency,
          reason: 'presentationAmount-missing',
        }
      : null,
  };
}

function findLedgerReport(reportLineAuthorship, reportType) {
  const reports = Array.isArray(reportLineAuthorship?.reports) ? reportLineAuthorship.reports : [];
  return reports.find((report) => report?.reportType === reportType) || null;
}

function sectionLabel(section) {
  const key = String(section || 'unclassified').toLowerCase();
  if (key === 'assets') return 'Assets';
  if (key === 'liabilities') return 'Liabilities';
  if (key === 'equity') return 'Equity';
  if (key === 'revenue') return 'Revenue';
  if (key === 'expenses') return 'Expenses';
  if (key === 'operating') return 'Operating';
  if (key === 'investing') return 'Investing';
  if (key === 'financing') return 'Financing';
  return 'Ledger';
}

function normalizeLedgerReportRows(report, reportType, options = {}) {
  if (!report || !Array.isArray(report.lines)) return [];
  return report.lines.map((line, index) => {
    const section = String(line?.section || line?.cashFlowClass || 'ledger').toLowerCase();
    const account = line?.account || null;
    const amountSelection = reportLineAmountSelection(line, options);
    return {
      id: line?.id || `${reportType}:${index}`,
      accountId: account?.id || null,
      name: line?.label || account?.name || account?.code || `Ledger row ${index + 1}`,
      cat: sectionLabel(section),
      section,
      amount: amountSelection.amount,
      currency: amountSelection.currency,
      source: 'ledger-contract',
      rowAuthorship: line?.proofPosture?.rowAuthorship || line?.sourceLineage?.sourceClass || null,
      moneyProvenance: amountSelection.moneyProvenance,
      __moneyProvenance: amountSelection.moneyProvenance,
      __ledgerAmountBasis: amountSelection.basis,
      __ledgerAmountCurrency: amountSelection.currency,
      __ledgerUnconvertedReportingCurrency: amountSelection.unconvertedReportingCurrency,
      __ledgerAggregateRow: line,
      __ledgerAggregateReportType: reportType,
      __ledgerAggregateReportLabel: report?.label || reportType,
      __ledgerAggregateFormalLineItemsHeld: ['income-statement', 'balance-sheet', 'cash-flow'].includes(reportType),
      __ledgerAggregateFormalLineItemHeldReason: 'ledger.json v1 emits service aggregate rows for formal statements',
    };
  });
}

function sumLedgerRows(rows, predicate = () => true) {
  return rows.reduce((sum, row) => sum + (predicate(row) ? ledgerMoneyNumber(row.amount) : 0), 0);
}

function rollupMoney(row, keys, fallback = 0) {
  for (const key of keys) {
    if (row?.[key] == null || row?.[key] === '') continue;
    return ledgerMoneyNumber(row[key]);
  }
  return fallback;
}

function makeRollupOnlyActualsSnapshot(json, { source, rollups }) {
  const safeRollups = Array.isArray(rollups) ? rollups : [];
  const last = safeRollups[safeRollups.length - 1] || null;
  const assetsLiquid = rollupMoney(last, ['liquidEnding', 'assetsLiquid', 'cashEnding', 'cashEndingBalance', 'cash']);
  const fixedAssetsNet = rollupMoney(last, ['fixedAssetsNet', 'nonCurrentAssets', 'ncAssets']);
  const totalAssets = rollupMoney(last, ['totalAssets', 'assetsEnding', 'assets'], assetsLiquid + fixedAssetsNet);
  const ccDebt = rollupMoney(last, ['ccDebtEnding', 'ccDebt', 'shortTermDebt', 'currentDebt']);
  const ltl = rollupMoney(last, ['ltl', 'longTermLiabilities', 'longTermDebt']);
  const totalLiabilities = rollupMoney(last, ['totalLiabilities', 'liabilitiesEnding', 'liabilities'], ccDebt + ltl);
  const equity = rollupMoney(last, ['totalEquity', 'equity', 'equityEnding'], totalAssets - totalLiabilities);
  const revenue = rollupMoney(last, ['revenue', 'totalRevenue', 'fvfRevenue']);
  const expenses = rollupMoney(last, ['expenses', 'totalExpenses']);
  const netIncome = rollupMoney(last, ['netIncome', 'profit', 'netProfit'], revenue - expenses);
  const netCashChange = rollupMoney(last, ['netCashChange', 'netFCF', 'freeCashFlow', 'netCashFlow'], netIncome);
  const bookScope = json?.meta?.bookScope || json?.bookScope || makeActiveBookScopePatch();
  const bookSettings = json?.meta?.bookSettings || json?.bookSettings || null;
  return {
    source,
    scopeKind: bookScope?.activeScope || (_activeShelfId ? 'shelf' : 'book'),
    bookScope,
    bookSettings,
    currency: bookSettings?.reportingCurrency || bookSettings?.defaultCurrency || json?.meta?.baseCurrency || null,
    generatedAt: json?.meta?.generatedAt || null,
    asOfDate: json?.meta?.asOfDate || null,
    coverageStart: json?.meta?.coverageStart || null,
    coverageEnd: json?.meta?.coverageEnd || null,
    empty: safeRollups.length === 0,
    rollupOnly: true,
    balanceSheetRows: [],
    incomeStatementRows: [],
    cashFlowRows: [],
    totals: {
      assetsLiquid,
      fixedAssetsNet,
      totalAssets,
      ccDebt,
      ccCredits: 0,
      ltl,
      totalLiabilities,
      equity,
      susTotal: 0,
      netWorthProxy: totalAssets - totalLiabilities,
      revenue,
      expenses,
      netIncome,
      netCashChange,
    },
    rowCounts: {
      balanceSheet: 0,
      incomeStatement: 0,
      cashFlow: 0,
      rollups: safeRollups.length,
    },
  };
}

function deriveActualsSnapshotFromLedgerContract(json, { source, rollups }) {
  const reportLineAuthorship = json?.meta?.reportLineAuthorship || json?.reportLineAuthorship || null;
  if (!reportLineAuthorship) return makeRollupOnlyActualsSnapshot(json, { source, rollups });
  const bookScope = json?.meta?.bookScope || json?.bookScope || reportLineAuthorship.bookScope || makeActiveBookScopePatch();
  const bookSettings = json?.meta?.bookSettings || json?.bookSettings || reportLineAuthorship.bookSettings || null;
  const rowOptions = { bookSettings, baseCurrency: json?.meta?.baseCurrency || null };

  const incomeStatementRows = normalizeLedgerReportRows(
    findLedgerReport(reportLineAuthorship, 'income-statement'),
    'income-statement',
    rowOptions,
  );
  const balanceSheetRows = normalizeLedgerReportRows(
    findLedgerReport(reportLineAuthorship, 'balance-sheet'),
    'balance-sheet',
    rowOptions,
  );
  const cashFlowRows = normalizeLedgerReportRows(
    findLedgerReport(reportLineAuthorship, 'cash-flow'),
    'cash-flow',
    rowOptions,
  );

  const assetRows = balanceSheetRows.filter((row) => row.section === 'assets');
  const liabilityRows = balanceSheetRows.filter((row) => row.section === 'liabilities');
  const equityRows = balanceSheetRows.filter((row) => row.section === 'equity');
  const liquidRows = assetRows.filter((row) => isLiquidLedgerAccount(row.__ledgerAggregateRow?.account));
  const cardRows = liabilityRows.filter((row) => isCardLedgerLiability(row.__ledgerAggregateRow?.account));
  const liquidTotal = liquidRows.length > 0 ? sumLedgerRows(liquidRows) : 0;
  const totalAssets = sumLedgerRows(assetRows);
  const ccDebt = sumLedgerRows(cardRows);
  const totalLiabilities = sumLedgerRows(liabilityRows);
  const revenue = sumLedgerRows(incomeStatementRows, (row) => row.section === 'revenue');
  const expenses = sumLedgerRows(incomeStatementRows, (row) => row.section === 'expenses');
  const netIncome = revenue - expenses;
  return {
    source,
    scopeKind: bookScope?.activeScope || (_activeShelfId ? 'shelf' : 'book'),
    bookScope,
    bookSettings,
    currency: bookSettings?.reportingCurrency || bookSettings?.defaultCurrency || json?.meta?.baseCurrency || null,
    generatedAt: json?.meta?.generatedAt || reportLineAuthorship.generatedAt || null,
    asOfDate: json?.meta?.asOfDate || null,
    coverageStart: json?.meta?.coverageStart || null,
    coverageEnd: json?.meta?.coverageEnd || null,
    empty: balanceSheetRows.length === 0 && incomeStatementRows.length === 0 && cashFlowRows.length === 0,
    rollupOnly: false,
    balanceSheetRows,
    incomeStatementRows,
    cashFlowRows,
    totals: {
      assetsLiquid: liquidTotal,
      fixedAssetsNet: Math.max(0, totalAssets - liquidTotal),
      totalAssets,
      ccDebt,
      ccCredits: 0,
      ltl: Math.max(0, totalLiabilities - ccDebt),
      totalLiabilities,
      equity: sumLedgerRows(equityRows),
      susTotal: 0,
      netWorthProxy: totalAssets - totalLiabilities,
      revenue,
      expenses,
      netIncome,
      netCashChange: sumLedgerRows(cashFlowRows),
    },
    rowCounts: {
      balanceSheet: balanceSheetRows.length,
      incomeStatement: incomeStatementRows.length,
      cashFlow: cashFlowRows.length,
      rollups: Array.isArray(rollups) ? rollups.length : 0,
    },
  };
}

function currentLedgerYM() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function addLedgerMonths(ym, months) {
  const [year, month] = String(ym || '').split('-').map(Number);
  if (!year || !month) return currentLedgerYM();
  const date = new Date(year, month - 1 + months, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/* Derive monthly rollups from the contract's journalEntries[] when the
   ledger payload doesn't ship pre-computed monthlyRollups. Minimal shape —
   the engine handles category splits; here we just seed the trajectory
   fields Lens needs to not break. */
function deriveRollupsFromJournalEntries(entries, accounts) {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const accountById = new Map((accounts || []).map((a) => [a.id, a]));
  const byYM = {};
  for (const e of entries) {
    const ym = String(e.date || '').slice(0, 7);
    if (!ym || ym.length !== 7) continue;
    const row = byYM[ym] = byYM[ym] || {
      ym,
      liquidNet: 0,
      ccDebtNet: 0,
      revenue: 0,
      entriesInMonth: 0,
    };
    row.entriesInMonth += 1;
    for (const line of e.lines || []) {
      const acct = accountById.get(line.accountId);
      if (!acct) continue;
      const signedAmount = line.amountInDefault
        ? ledgerMoneyNumber(line.amountInDefault)
        : line.signedAmount
          ? ledgerMoneyNumber(line.signedAmount)
          : ledgerMoneyNumber(line.debit) - ledgerMoneyNumber(line.credit);
      if (isLiquidLedgerAccount(acct)) row.liquidNet += signedAmount;
      if (isCardLedgerLiability(acct)) row.ccDebtNet += -signedAmount;
      if (ledgerAccountType(acct) === 'income') row.revenue += -signedAmount;
    }
  }
  const months = Object.values(byYM).sort((a, b) => a.ym.localeCompare(b.ym));
  if (months.length === 0) return null;
  const monthByYM = new Map(months.map((row) => [row.ym, row]));
  const endYM = currentLedgerYM() > months[months.length - 1].ym
    ? currentLedgerYM()
    : months[months.length - 1].ym;
  let liquid = 0;
  let ccDebt = 0;
  const out = [];
  for (let ym = months[0].ym, guard = 0; ym <= endYM && guard < 240; ym = addLedgerMonths(ym, 1), guard += 1) {
    const m = monthByYM.get(ym) || { ym, liquidNet: 0, ccDebtNet: 0, revenue: 0, entriesInMonth: 0 };
    liquid += m.liquidNet;
    ccDebt += m.ccDebtNet;
    out.push({
      ym: m.ym,
      liquidEnding: liquid,
      ccDebtEnding: Math.max(0, ccDebt),
      netFCF: m.liquidNet,
      fvfRevenue: m.revenue,
      entriesInMonth: m.entriesInMonth,
    });
  }
  return out;
}

/* Try the configured data source. Returns the rollups array if found, else null. */
async function tryLoadLedger() {
  if (!_activeBookId && !_activeShelfId) {
    return markLensScopeHold();
  }

  // P-019: pull bearer once for both /api/books and /api/ledger.json calls.
  const bearer = LEDGER_SOURCE_MODE === 'api' ? await getBearerHeader() : null;
  if (LEDGER_SOURCE_MODE === 'api' && !bearer) {
    return markApiContractHold('no private auth session for contract source');
  }

  if (LEDGER_SOURCE_MODE === 'api' && bearer && !_activeBookId && !_activeShelfId) {
    return markApiContractHold(
      'strict API mode requires explicit Lens Book or Shelf scope before loading contract source',
      'Explicit Book or Shelf scope is required before Lens can load strict Ledger contract actuals.'
    );
  }

  _providerHealth = await fetchProviderHealth(bearer);

  const candidates = [];
  if (LEDGER_SOURCE_MODE === 'api') {
    candidates.push({ kind: 'contract', url: `${LEDGER_API_URL.replace(/\/+$/, '')}/api/ledger.json` });
  } else {
    candidates.push({ kind: 'snapshot', url: '/ledger.json' });
    candidates.push({ kind: 'snapshot', url: '/ledger-monthly.json' });
  }

  for (const { kind, url } of candidates) {
    try {
      // Inject Bearer + x-book-id only on contract (cross-origin auth-required)
      // calls; snapshot URLs are same-origin static files served by Vercel.
      const headers = kind === 'contract' ? makeLedgerHeaders(bearer) : {};

      const res = await fetch(url, { cache: 'no-cache', headers });
      if (!res.ok) {
        if (kind === 'contract') _contractFallbackReason = `contract request returned HTTP ${res.status}`;
        continue;
      }
      const json = await res.json();
      const contractProof = kind === 'contract' ? extractContractProof(json) : {};
      const commercialTimingSignals = Array.isArray(json?.commercialTimingSignals)
        ? json.commercialTimingSignals
        : [];
      setLedgerCommercialTimingSignals(commercialTimingSignals, kind);

      // F-022 breadcrumb so the operator can see which pipe lit up.
      if (kind === 'contract') {
        const entryCount   = json?.meta?.entryCount   ?? (json?.journalEntries?.length ?? 0);
        const accountCount = json?.meta?.accountCount ?? (json?.accounts?.length ?? 0);
        console.info(`[data-ledger] contract tier: connected to ${json?.meta?.source || 'cleic-ledger'} — ${accountCount} accounts, ${entryCount} entries`);
        if (entryCount === 0) {
          _contractFallbackReason = 'contract payload returned no ledger entries';
          console.info('[data-ledger] contract tier returned empty ledger — expected for a fresh tenant/book. Strict API mode will not load fallback actuals.');
        }
      }

      // Preferred: pre-computed monthlyRollups (contract or snapshot).
      if (Array.isArray(json?.monthlyRollups) && json.monthlyRollups.length > 0) {
        const actualsSnapshot = kind === 'contract'
          ? deriveActualsSnapshotFromLedgerContract(json, { source: kind, rollups: json.monthlyRollups })
          : makeRollupOnlyActualsSnapshot(json, { source: kind, rollups: json.monthlyRollups });
        _ledgerSourceState = makeLedgerSourceState(kind, {
          ...contractProof,
          providerHealth: _providerHealth,
          fallbackReason: kind === 'contract' ? null : _contractFallbackReason,
          warning: kind === 'contract' && (contractProof.bookScope?.autoPickedBook ?? _activeBookWasAutoPicked)
            ? 'Contract Book scope is not explicit; label as heuristic/private, not strict proof.'
            : undefined,
        });
        _ledgerActualsSnapshot = actualsSnapshot;
        return { rollups: json.monthlyRollups, source: kind, sourceState: _ledgerSourceState, actuals: actualsSnapshot };
      }
      // Bare-array snapshot format (legacy /ledger-monthly.json).
      if (Array.isArray(json) && json[0]?.ym) {
        setLedgerCommercialTimingSignals([], kind);
        const actualsSnapshot = makeRollupOnlyActualsSnapshot({ meta: {} }, { source: kind, rollups: json });
        _ledgerSourceState = makeLedgerSourceState(kind, {
          ...contractProof,
          providerHealth: _providerHealth,
          fallbackReason: kind === 'contract' ? null : _contractFallbackReason,
        });
        _ledgerActualsSnapshot = actualsSnapshot;
        return { rollups: json, source: kind, sourceState: _ledgerSourceState, actuals: actualsSnapshot };
      }
      // Derive rollups from journalEntries if present.
      if (Array.isArray(json?.journalEntries) && json.journalEntries.length > 0) {
        const derived = deriveRollupsFromJournalEntries(json.journalEntries, json.accounts);
        if (derived && derived.length > 0) {
          const actualsSnapshot = kind === 'contract'
            ? deriveActualsSnapshotFromLedgerContract(json, { source: kind, rollups: derived })
            : makeRollupOnlyActualsSnapshot(json, { source: kind, rollups: derived });
          console.info(`[data-ledger] derived ${derived.length} monthly rollups from ${json.journalEntries.length} journal entries.`);
          _ledgerSourceState = makeLedgerSourceState(kind, {
            ...contractProof,
            providerHealth: _providerHealth,
            fallbackReason: kind === 'contract' ? null : _contractFallbackReason,
            warning: kind === 'contract' && (contractProof.bookScope?.autoPickedBook ?? _activeBookWasAutoPicked)
              ? 'Contract Book scope is not explicit; label as heuristic/private, not strict proof.'
              : undefined,
          });
          _ledgerActualsSnapshot = actualsSnapshot;
          return { rollups: derived, source: kind, sourceState: _ledgerSourceState, actuals: actualsSnapshot };
        }
      }
      if (kind === 'contract' && LEDGER_SOURCE_MODE === 'api') {
        const actualsSnapshot = deriveActualsSnapshotFromLedgerContract(json, { source: kind, rollups: [] });
        _ledgerSourceState = makeLedgerSourceState('contract', {
          ...contractProof,
          providerHealth: _providerHealth,
          fallbackReason: null,
          warning: 'Ledger contract loaded for explicit Book scope, but no monthly rollups or journal rows were emitted. No snapshot or synthetic fallback loaded.',
        });
        _ledgerActualsSnapshot = actualsSnapshot;
        return { rollups: [], source: kind, sourceState: _ledgerSourceState, actuals: actualsSnapshot };
      }
      /* Payload present but empty — snapshot modes may try the next candidate. */
    } catch {
      if (kind === 'contract') _contractFallbackReason = 'contract fetch failed or returned invalid JSON';
      /* fetch fails or invalid JSON — try next candidate */
    }
  }
  return null;
}

/* Main entry — idempotent, safe to call multiple times. Populates cache
   + source. The app calls once at startup; engine.js reads the cache
   synchronously afterward via getMonthlyLedgerCache(). */
export async function loadMonthlyLedger({ force = false } = {}) {
  if (force) resetLedgerLoadCaches(LEDGER_SOURCE_MODE === 'api' ? 'contract' : 'snapshot');
  const existing = _sharedGetCache();
  if (existing) return existing;
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    const result = await tryLoadLedger();
    if (result) {
      setMonthlyLedgerCache(result.rollups, result.source);
      if (result.sourceState) _ledgerSourceState = result.sourceState;
      _ledgerActualsSnapshot = result.actuals || null;
      return result.rollups;
    }
    if (!_activeBookId && !_activeShelfId) {
      markLensScopeHold();
      setMonthlyLedgerCache(null, LEDGER_SOURCE_MODE === 'api' ? 'contract' : 'snapshot');
      return null;
    }
    /* Tier 3 — caller falls back to synthetic drift. */
    if (LEDGER_SOURCE_MODE === 'api') {
      markApiContractHold(_contractFallbackReason || 'contract source unavailable in strict API mode');
      setMonthlyLedgerCache(null, 'contract');
      return null;
    }
    _ledgerSourceState = makeLedgerSourceState('synthetic', {
      fallbackReason: _contractFallbackReason || 'contract and snapshot tiers unavailable',
      warning: 'Synthetic-derived local model only; no provider, raw-row, public, or strict real-book proof.',
    });
    setMonthlyLedgerCache(null, 'synthetic');
    setLedgerCommercialTimingSignals(null, 'synthetic');
    return null;
  })();
  return _loadPromise;
}
